import { useRouter } from "expo-router";
import { ReactNode } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";

import {
  CheckCircle2,
  FolderOpen,
  LayoutDashboard,
} from "../../../components/central-icons";
import { central, semantic, spacing } from "../../../theme/tokens";
import { arrangementRoutes } from "../routes";
import { getMobileTableColumnIndexes } from "../utils/admin-layout";

export type IconComponent = (props: {
  color: string;
  size: number;
  strokeWidth?: number;
}) => ReactNode;

export function WorkspaceNotFound({ screenId }: { screenId: string }) {
  const router = useRouter();
  return (
    <View style={styles.stack}>
      <View style={styles.panel}>
        <Text style={styles.screenEyebrow}>CENTRAL</Text>
        <Text accessibilityRole="header" style={styles.screenTitle}>Screen not found</Text>
        <Text style={styles.screenSubtitle}>
          “{screenId}” is not a Central workspace screen. It may have moved, or the link may be out of date.
        </Text>
        <View style={styles.headerButtonRow}>
          <SecondaryButton label="Open Today" icon={LayoutDashboard} onPress={() => router.push("/" as never)} />
          <SecondaryButton label="Go to Cases" icon={FolderOpen} onPress={() => router.push("/workspace/case-list" as never)} />
        </View>
      </View>
    </View>
  );
}

export function DataTable({
  columns,
  rows,
  isDesktop,
  routeMode = "case",
  onRowPress,
  rowAccessibilityLabel,
}: {
  columns: readonly string[];
  rows: readonly (readonly string[])[];
  isDesktop: boolean;
  routeMode?: "case" | "template-editor" | "none";
  onRowPress?: (row: readonly string[], index: number) => void;
  rowAccessibilityLabel?: (row: readonly string[]) => string;
}) {
  const router = useRouter();
  const routeForRow = (row: readonly string[]) =>
    rowRouteForMode(row, routeMode);

  if (!isDesktop) {
    const mobileColumnIndexes = getMobileTableColumnIndexes(columns);
    return (
      <View style={styles.mobileTableStack}>
        {rows.map((row, rowIndex) => {
          const key = row[0] ?? row.join("-");
          const route = routeForRow(row);
          const displayTitle = rowDisplayTitle(columns, row);
          if (!route && !onRowPress) {
            return (
              <View key={key} style={styles.mobileDataCard}>
                <Text style={styles.mobileDataTitle}>{displayTitle}</Text>
                <View style={styles.mobileDataGrid}>
                  {mobileColumnIndexes.map((index) => (
                    <View
                      key={`${key}-${columns[index]}`}
                      style={styles.mobileDataPair}
                    >
                      <Text style={styles.mobileDataLabel}>{columns[index]}</Text>
                      <Text style={styles.mobileDataValue}>
                        {row[index] ?? "-"}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            );
          }
          return (
            <Pressable
              key={key}
              accessibilityRole="button"
              accessibilityLabel={
                rowAccessibilityLabel?.(row) ?? `Open ${displayTitle}`
              }
              onPress={() => {
                if (onRowPress) {
                  onRowPress(row, rowIndex);
                } else if (route) {
                  router.push(route as never);
                }
              }}
              style={styles.mobileDataCard}
            >
              <Text style={styles.mobileDataTitle}>{displayTitle}</Text>
              <View style={styles.mobileDataGrid}>
                {mobileColumnIndexes.map((index) => (
                  <View key={`${key}-${columns[index]}`} style={styles.mobileDataPair}>
                    <Text style={styles.mobileDataLabel}>{columns[index]}</Text>
                    <Text style={styles.mobileDataValue}>
                      {row[index] ?? "-"}
                    </Text>
                  </View>
                ))}
              </View>
            </Pressable>
          );
        })}
      </View>
    );
  }

  return (
    <View style={styles.table}>
      <View style={styles.tableHeader}>
        {columns.map((column) => (
          <Text key={column} style={styles.tableHeaderText}>
            {column}
          </Text>
        ))}
      </View>
      {rows.map((row, rowIndex) => {
        const key = row[0] ?? row.join("-");
        const route = routeForRow(row);
        if (!route && !onRowPress) {
          return (
            <View key={key} style={styles.tableRow}>
              {columns.map((column, index) => (
                <Text
                  key={`${key}-${column}`}
                  style={[
                    styles.tableCell,
                    index === 1 ? styles.tableCellStrong : null,
                  ]}
                >
                  {row[index] ?? "-"}
                </Text>
              ))}
            </View>
          );
        }
        return (
          <Pressable
            key={key}
            accessibilityRole="button"
            accessibilityLabel={
              rowAccessibilityLabel?.(row) ??
              `Open ${rowDisplayTitle(columns, row)}`
            }
            onPress={() => {
              if (onRowPress) {
                onRowPress(row, rowIndex);
              } else if (route) {
                router.push(route as never);
              }
            }}
            style={styles.tableRow}
          >
            {columns.map((column, index) => (
              <Text
                key={`${key}-${column}`}
                style={[
                  styles.tableCell,
                  index === 1 ? styles.tableCellStrong : null,
                ]}
              >
                {row[index] ?? "-"}
              </Text>
            ))}
          </Pressable>
        );
      })}
    </View>
  );
}

export function PrimaryButton({
  label,
  icon: Icon,
  onPress,
  fullWidth,
  large,
  disabled = false,
  accessibilityHint,
}: {
  label: string;
  icon: IconComponent;
  onPress: () => void;
  fullWidth?: boolean;
  large?: boolean;
  disabled?: boolean;
  accessibilityHint?: string;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityHint={accessibilityHint}
      accessibilityState={{ disabled }}
      disabled={disabled}
      onPress={onPress}
      style={[
        styles.primaryButton,
        large ? styles.primaryButtonLarge : null,
        fullWidth ? styles.fullWidth : null,
        disabled ? styles.buttonDisabled : null,
      ]}
    >
      <Icon color={disabled ? central.text.muted : central.text.inverse} size={16} strokeWidth={2.2} />
      <Text style={styles.primaryButtonText}>{label}</Text>
    </Pressable>
  );
}

export function SecondaryButton({
  label,
  icon: Icon,
  onPress,
  fullWidth,
  disabled = false,
  accessibilityHint,
}: {
  label: string;
  icon: IconComponent;
  onPress: () => void;
  fullWidth?: boolean;
  disabled?: boolean;
  accessibilityHint?: string;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityHint={accessibilityHint}
      accessibilityState={{ disabled }}
      disabled={disabled}
      onPress={onPress}
      style={[
        styles.secondaryButton,
        fullWidth ? styles.fullWidth : null,
        disabled ? styles.buttonDisabled : null,
      ]}
    >
      <Icon color={disabled ? central.text.muted : central.action.primary} size={16} strokeWidth={2.1} />
      <Text style={styles.secondaryButtonText}>{label}</Text>
    </Pressable>
  );
}

export function CheckLine({ label }: { label: string }) {
  return (
    <View style={styles.checkLine}>
      <CheckCircle2 color={central.status.success.foreground} size={17} strokeWidth={2} />
      <Text style={styles.checkLineText}>{label}</Text>
    </View>
  );
}

function rowRouteForMode(
  row: readonly string[],
  routeMode: "case" | "template-editor" | "none",
) {
  if (routeMode === "none") return undefined;
  if (routeMode === "template-editor")
    return arrangementRoutes.systemPage("form-template-editor");
  if (row[0] && /^[A-Za-z0-9_-]{4,120}$/.test(row[0]))
    return `${arrangementRoutes.systemPage("case-overview")}?caseId=${encodeURIComponent(row[0])}`;
  return undefined;
}

function rowDisplayTitle(columns: readonly string[], row: readonly string[]) {
  if (columns[0] === "Date" && row[1]) return row[1];
  if (columns[0] === "Case" && columns[1] === "Loved one" && row[1])
    return row[1];
  return row[0] ?? row[1] ?? "Record";
}

const styles = StyleSheet.create({
  stack: {
    flexDirection: "column",
  },
  panel: {
    minWidth: 0,
    maxWidth: "100%",
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: central.radius.card,
    backgroundColor: central.surface.raised,
    borderWidth: 1,
    borderColor: central.border.subtle,
  },
  screenEyebrow: {
    color: central.action.primary,
    fontFamily: central.typography.label,
    fontSize: 11,
    lineHeight: 15,
    textTransform: "uppercase",
  },
  screenTitle: {
    color: central.text.primary,
    fontFamily: central.typography.display,
    fontSize: 24,
    lineHeight: 31,
  },
  screenSubtitle: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 13,
    lineHeight: 20,
    maxWidth: 760,
  },
  headerButtonRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    flexShrink: 1,
  },
  table: {
    borderWidth: 1,
    borderColor: central.border.subtle,
    borderRadius: central.radius.control,
    overflow: "hidden",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: central.surface.muted,
  },
  tableHeaderText: {
    flex: 1,
    minWidth: 0,
    paddingVertical: 9,
    paddingHorizontal: 8,
    color: semantic.text.secondary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 10,
    lineHeight: 14,
    textTransform: "uppercase",
  },
  tableRow: {
    minHeight: 54,
    flexDirection: "row",
    alignItems: "center",
    borderTopWidth: 1,
    borderTopColor: central.border.subtle,
    backgroundColor: central.surface.raised,
  },
  tableCell: {
    flex: 1,
    minWidth: 0,
    paddingVertical: 9,
    paddingHorizontal: 8,
    color: semantic.text.secondary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 11,
    lineHeight: 16,
  },
  tableCellStrong: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
  },
  mobileTableStack: {
    gap: 10,
  },
  mobileDataCard: {
    minWidth: 0,
    maxWidth: "100%",
    gap: 10,
    padding: 12,
    borderRadius: 8,
    backgroundColor: central.surface.muted,
    borderWidth: 1,
    borderColor: central.border.subtle,
  },
  mobileDataTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 14,
    lineHeight: 19,
  },
  mobileDataGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  mobileDataPair: {
    width: "100%",
    minWidth: 0,
    gap: 2,
  },
  mobileDataLabel: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 11,
    lineHeight: 15,
    textTransform: "uppercase",
  },
  mobileDataValue: {
    color: semantic.text.primary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 12,
    lineHeight: 17,
  },
  primaryButton: {
    minHeight: 44,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    paddingHorizontal: 14,
    borderRadius: central.radius.control,
    backgroundColor: central.action.primary,
    flexShrink: 1,
  },
  primaryButtonLarge: {
    minHeight: 50,
  },
  primaryButtonText: {
    flexShrink: 1,
    textAlign: "center",
    color: central.text.inverse,
    fontFamily: central.typography.label,
    fontSize: 12,
    lineHeight: 17,
  },
  secondaryButton: {
    minHeight: 44,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    paddingHorizontal: 14,
    borderRadius: central.radius.control,
    backgroundColor: central.surface.raised,
    borderWidth: 1,
    borderColor: central.border.default,
    flexShrink: 1,
  },
  secondaryButtonText: {
    flexShrink: 1,
    textAlign: "center",
    color: central.action.primary,
    fontFamily: central.typography.label,
    fontSize: 12,
    lineHeight: 17,
  },
  buttonDisabled: {
    opacity: 0.48,
  },
  fullWidth: {
    width: "100%",
  },
  checkLine: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  checkLineText: {
    flex: 1,
    color: central.text.primary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 12,
    lineHeight: 18,
  },
});
